// **The `/demo` composition root** (`demo-presentation-shell` task 2.2, design **DD1**). Owns the
// whole presentation for its lifetime: the cue cursor (`sequencer.ts`), the derived UI/HUD
// visibility (`ui-state.ts`) and the fullscreen-on-first-interaction request. It renders **no**
// debug, cue-index, cue-kind, chapter-number or developer chrome — every presenter affordance is a
// keystroke, never an on-screen control (requirement "A presenter-only demo surface at `/demo`").
//
// **What is mounted here** (completed at group 12/13's integration): the real slide deck (group 4),
// the real `DemoStage` and its pointer interactions (groups 6/7), the cue-gated HUD and both modals
// (group 8), the B++ type editor (group 9), the two agent prompt bars (group 10) and the three C-3
// tournament chapters (group 12). Which of them is on screen is **derived** from the cursor by
// `ui-state.ts`'s `deriveScene` — never read off the cue at the cursor, because a `reveal` steps
// the slide already up and a `ui` cue arrives over the world already running.
//
// **Every seam is injected with no default.** Mirrors the `AmbientBackground`/`CanvasRenderer`
// factory precedent (design DD4/DD3): the stage driver, the renderer factory, the tournament
// transports and the two agent transports are all `undefined` unless a composition root supplies
// them (`DemoRoot.tsx` is the production one). A cue crossed with nothing wired up is inert rather
// than throwing, exactly like a bare `AmbientBackground` mount with no renderer factory.

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createDemoSequencer } from './sequencer';
import type { DemoSequencer } from './sequencer';
import { CUE_LIST, configureAnchorIndex } from './cues';
import type { ChapterId, Cue, UiToken } from './cues';
import { deriveScene, deriveUiState } from './ui-state';
import type { ChapterTitleToken } from './ui-state';
import { renderSlide } from './slides/SlideDeck';
import { DemoStage, stageFrameOf } from './DemoStage';
import type { DemoStageProps } from './DemoStage';
import { isTextEntryFocused } from './text-entry-guard';
import { createBrowserFullscreenApi } from './fullscreen';
import type { FullscreenApi } from './fullscreen';
import { nextDemoClockRate, DEFAULT_DEMO_CLOCK_RATE, DEMO_TICK_HZ } from './stage-clock';
import type { DemoClockRate } from './stage-clock';
import type { DemoStageDriver, DemoStageTypeSpec, DemoVocabEntry } from './stage-driver';
import { CHAPTER_TITLES, ChapterTitleOverlay, DemoCounters } from './DemoHud';
import { BuilderFlowchartModal, FullVocabularyModal, SixActionsModal } from './ApiModals';
import { TypeBar, demoTypeBody } from './TypeEditor';
import { InstructPromptBar } from './InstructPromptBar';
import type { InstructPromptBarProps } from './InstructPromptBar';
import { BeekeeperPromptBar } from './BeekeeperPromptBar';
import type { BeekeeperPromptBarProps } from './BeekeeperPromptBar';
import { CONTINUOUS_STAGE, HARVESTER_PROGRAM } from './chapters';
import { createPresentationAudio, type PresentationAudioFactory } from './presentation-audio';
import { DemoTournamentChapters } from './DemoTournamentChapters';
import type { DemoTournamentSeams } from './DemoTournamentChapters';
import { preloadDeckDiagrams } from './slides/MermaidDiagram';
import { DemoLiveMatchChapters, type DemoLiveMatchSeams } from './DemoLiveMatchChapters';
import { FESTIVAL_ASSETS } from './festival-assets';
import { Z, Z_LOCAL } from './z-layers';

/** Resolves every `DemoStageDriver` method to a no-op — what the sequencer is given when no real
 * driver has been injected, so advancing past a `stage` cue with nothing wired up degrades
 * silently rather than throwing (the `AmbientBackground` precedent, applied to the stage side). */
const INERT_STAGE_DRIVER: DemoStageDriver = {
  configure: () => Promise.resolve(),
  spawn: () => Promise.resolve(),
  hotSwap: () => Promise.resolve(),
  fastForward: () => Promise.resolve(),
  command: () => Promise.resolve(),
  submitCommand: () => Promise.resolve(),
};

/** The script-copy chord's write target. Narrowed to the one method used, mirroring
 * `FullscreenApi`'s own narrow-seam precedent — jsdom has no real clipboard, so this is injected
 * rather than read off `navigator.clipboard` directly, the same reason `fullscreen` is a prop. */
export interface ClipboardApi {
  writeText(text: string): Promise<void>;
}

/**
 * **"I've forgotten the script"** — the current chapter's canned program, or `null` if the chapter
 * has none to copy. Read off `chapters.ts`, never restated: chapter 1 is point-and-click (nothing
 * is ever typed), and chapter 3's beat is a spoken prompt to the Beekeeper, not a B++ program the
 * presenter authors by hand — so both are deliberately `null` rather than a copy of the wrong
 * thing. Chapter 2's is the one real case: `HARVESTER_PROGRAM`, the seek_flowers/gather/deliver/
 * unload forage loop the beat manifest's `stage-chapter2-run-harvester-program` names. Chapter 2's
 * own stage cannot supply it — that chapter opens on an idling placeholder by design, because its
 * whole beat is typing the behaviour in — so the constant is read **by name** from `chapters.ts`.
 * It used to be reached as `CHAPTER3_FULL_HIVE_STAGE.colonies[0].types.find(name === 'harvester')`,
 * which resolved to the same text but bound the presenter's recovery to chapter 3's roster: that
 * stage now registers all five presets, and re-pointing its harvester at a `waggle-fixtures` preset
 * source would have silently changed what this chord hands the presenter with no test failing.
 *
 * Returned as a **body**, via `demoTypeBody`, because the paste target is the editor's draft
 * textarea, which holds a body — `TypeEditor`'s own `demoTypeDocument` composes the
 * `type ... buffs ...` header itself, and pasting a document that already carries one would double
 * it up on the next Run.
 */
function chapterCannedProgram(chapter: ChapterId | undefined): string | null {
  return chapter === 'chapter2' ? demoTypeBody(HARVESTER_PROGRAM) : null;
}

export interface DemoAppProps {
  /** No default (see module header) — group 5.7 threads the real worker-backed factory once it
   * exists. */
  createStageDriver?: () => DemoStageDriver;
  /** Defaults to the real browser Fullscreen API. */
  fullscreen?: FullscreenApi;
  /** The script-copy chord's (`Cmd`/`Ctrl`+`Shift`+`C`) write target. Defaults to
   * `navigator.clipboard`, which jsdom has none of — the same reason `fullscreen` above is a
   * prop. */
  clipboard?: ClipboardApi;
  /** Projector-only theatrical media; injectable so rehearsal tests never touch browser audio. */
  createAudio?: PresentationAudioFactory;
  /** The cue list to drive — defaults to the shipped script (`CUE_LIST`). Overridable so
   * `client-demo/the-cue-list-is-the-only-ordering` can drive a substitute list end to end. */
  cueList?: readonly Cue[];
  /**
   * The two agent surfaces' network seams, passed straight through — no logic of this component's
   * own. Production supplies a `baseUrl` and leaves the rest defaulted (a relative `POST` to the
   * demo's own origin, the real practice session, the real chat client). The **rehearsal
   * configuration** supplies transports instead: the show is walked against the server's canned
   * instruct and canned practice-agent paths with the client surfaces *unchanged*, which is the
   * requirement's own "what the presenter rehearses is what the presenter performs".
   */
  agents?: {
    baseUrl?: string;
    instruct?: Pick<InstructPromptBarProps, 'fetchImpl' | 'idGen'>;
    beekeeper?: Pick<
      BeekeeperPromptBarProps,
      'fetchImpl' | 'storage' | 'random' | 'openSession' | 'createClient'
    >;
  };
  /** What the live stage is drawn with: the worker's snapshot stream and the renderer factory.
   * Both optional with no default, the `AmbientBackground` precedent — a bare mount renders the
   * stage's chrome with no canvas and no world rather than reaching for a GPU context. */
  stage?: Pick<DemoStageProps, 'subscribe' | 'createRenderer' | 'camera' | 'frameScheduler' | 'clock'>;
  /** What the `nav` cues' three C-3 components are mounted with (design **DD9**, task 12.2).
   * Supplied by `DemoRoot` from the secret-custody flow's host membership credential; omitted, the
   * tournament chapters render their inert arm rather than reaching for a credential. */
  tournament?: DemoTournamentSeams;
  /** Presentation-owned Live Match orchestration. When present, it replaces the legacy
   * tournament chapters while leaving that product capability available to other roots. */
  presentation?: DemoLiveMatchSeams;
  /**
   * **Anything that must sit over the show** — the secret capture, the rebind prompt, the stage
   * failure band (`DemoRoot.tsx` composes all three).
   *
   * It is a *slot inside this component's own root* rather than a sibling of it, and that is the
   * whole point (E2E round-3 finding V10/C). `demo-root` is the element this component puts into
   * **fullscreen** on the first presenter interaction, and a fullscreen element is promoted to the
   * browser's **top layer** — above every sibling in the document, whatever its `z-index`. So the
   * rebind card, rendered beside `DemoApp`, was painted *under* the slide it was supposed to
   * interrupt: Playwright reported the slide's own `<h1>` intercepting every click at it, and the
   * key the presenter could not type went to the server empty. Inside the fullscreen element,
   * ordinary stacking applies again and the overlay's `z-index` means what it says.
   */
  overlay?: ReactNode;
}

const RESET_KEY = 'r';

/** The cue an accepted Start takes the projector to (`cues.ts`) — plan **D-3**. */
const LIVE_MATCH_BEAT = 'nav-pvp-live';

/** ...and where an accepted Reset takes it back to: the Red/Blue seat cards awaiting players.
 * `nav-pvp-waiting` rather than `nav-pvp-setup` because a reset leaves a *fresh match* behind, not
 * no match — the create form has nothing to ask for, and both cues render the same waiting room. */
const JOIN_WINDOW_BEAT = 'nav-pvp-waiting';

/** Cycles the clock-rate multiplier (1x -> 2x -> 4x -> 1x, `stage-clock.ts`'s own order).
 * Deliberately its own key, not the chapter-jump digits: those are the show's recovery mechanism
 * (`docs/dev/demo-presenter-guide.md`), and speed is a different concern entirely. */
const SPEED_KEY = 's';
const COVER_FADE_MS = 450;

/**
 * **How the cursor last moved** (playthrough finding, 2026-08-09) — the one fact about the
 * presentation that is *not* derivable from the cursor, because it is a property of the
 * transition rather than of the position. The chapter title needs it: entering a chapter announces
 * it, reversing through the same cue must not.
 */
type CursorMove = 'advance' | 'reverse' | 'jump' | 'reset';

/** The presenter's own colony, index 0 in every staged chapter (`chapters.ts`) — the same
 * convention `DemoStage`/`TypeEditor`/`BeekeeperPromptBar` already fix throughout this change. */
const DEMO_OWN_COLONY = 0;

/** The one type the staged world registers — the editor's subject, read off the stage content
 * rather than restated, so a rename in `chapters.ts` cannot leave the editor compiling a program
 * against a type the colony does not have. */
const STAGED_HARVESTER = CONTINUOUS_STAGE.colonies[0]?.types[0] ?? { name: 'harvester', buff: 'capacity' };

/** The three chapter-title chrome tokens, in cursor order — at most one is ever active at once
 * (`ui-state.ts`'s `visible` accumulates within a chapter, but only one chapter's title cue is
 * ever between the cursor's chapter-start and the cursor). */
const CHAPTER_TITLE_TOKENS = ['chapter1Title', 'chapter2Title', 'chapter3Title'] as const;

/** Every `ui` token this component gives a dedicated rendering to — the HUD's four elements, the
 * three chapter titles, and the two modals (rendered from `activeModal`, not looped generically).
 * Anything else `uiState.visible` carries (the rounds-mode chrome tokens later groups own) falls
 * back to the same untyped placeholder this component always rendered, so a token with no owner
 * yet still has *somewhere* to register as present rather than silently vanishing. */
const SPECIALLY_RENDERED_TOKENS: ReadonlySet<UiToken> = new Set([
  'nectarCounter',
  'beeCounter',
  'promptBar',
  'typeBar',
  ...CHAPTER_TITLE_TOKENS,
  'sixActionsModal',
  'fullVocabularyModal',
  'builderFlowchartModal',
  // Both are rendered by the leaderboard chapter itself (`DemoTournamentChapters`), from real
  // presentation props — never as a bare marker div, which is a beat that "passes" with nothing
  // on the projector.
  'winnersPrompts',
  'nextButton',
]);

export function DemoApp({
  createStageDriver,
  fullscreen,
  clipboard,
  createAudio = createPresentationAudio,
  cueList,
  agents,
  stage,
  tournament,
  presentation,
  overlay,
}: DemoAppProps): ReactElement {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const fullscreenApi = useMemo(() => fullscreen ?? createBrowserFullscreenApi(), [fullscreen]);
  const fullscreenRequestedRef = useRef(false);
  // `navigator.clipboard` is a stable global (unlike `createBrowserFullscreenApi()`, which builds
  // a fresh object), so this needs no memoization to stay referentially stable across renders.
  const clipboardApi = clipboard ?? (typeof navigator === 'undefined' ? undefined : navigator.clipboard);

  useEffect(() => {
    void preloadDeckDiagrams().catch((error: unknown) => {
      console.error('Unable to preload presentation diagrams', error);
    });
  }, []);

  const driverRef = useRef<DemoStageDriver | null>(null);
  const sequencerRef = useRef<DemoSequencer | null>(null);
  if (sequencerRef.current === null) {
    const driver = createStageDriver?.() ?? INERT_STAGE_DRIVER;
    driverRef.current = driver;
    sequencerRef.current = createDemoSequencer(driver, cueList ?? CUE_LIST);
  }
  const sequencer = sequencerRef.current;

  // The sequencer is a plain mutable object (its own state, not React state — DD2's "one
  // integer"), so a forced re-render is how its mutations become visible on screen.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const [exitingCover, setExitingCover] = useState(false);

  useEffect(() => {
    if (!exitingCover) return undefined;
    const handle = window.setTimeout(() => setExitingCover(false), COVER_FADE_MS);
    return () => window.clearTimeout(handle);
  }, [exitingCover]);

  /** Set by every mover of the cursor, read by the chapter title's entry rule. `advance` at mount
   * so a cue list that *opens* on a title still announces it. */
  const lastMoveRef = useRef<CursorMove>('advance');
  // Cursor operations may cross an async stage-driver boundary. Keep a second key delivery from
  // moving the mutable sequencer again before the first operation has settled and repainted the
  // scene. This also makes a duplicated global key listener harmless: the first listener claims
  // the transition synchronously, before either promise can yield.
  const cursorMoveInFlightRef = useRef(false);

  const moveCursor = (move: CursorMove, operation: () => Promise<void>): void => {
    if (cursorMoveInFlightRef.current) return;
    cursorMoveInFlightRef.current = true;
    const leavingCover = sequencer.currentSlide === 'slide-cover';
    lastMoveRef.current = move;
    void operation().then(
      () => {
        if (leavingCover && sequencer.currentSlide !== 'slide-cover') setExitingCover(true);
        cursorMoveInFlightRef.current = false;
        forceRender();
      },
      () => {
        cursorMoveInFlightRef.current = false;
        forceRender();
      },
    );
  };

  /**
   * The clock-rate multiplier `s` cycles.
   *
   * **State, not a ref** — and the reason is a defect that a ref hid. Nothing on screen shows the
   * rate (no visible control, the same rule the copy chord honours), which is why this began as a
   * ref. But the renderer's own presentation clock converts *real* elapsed milliseconds into ticks
   * (`present-clock.ts`'s `elapsedTicks = (nowMs - newestAtMs) * tickRateHz / 1000`), so it has to
   * be told how many world ticks a real second now buys. At the 2x default the worker emits 30
   * ticks a second while a renderer built at `DEMO_TICK_HZ` advanced its interpolation playhead at
   * 15 — so every snapshot re-anchored a playhead that was only half way there, and the whole
   * stage juddered, ~30 times a second, at the default. A ref cannot fix that because the renderer
   * is rebuilt from a `useEffect` and would never see the change.
   *
   * The demo already runs at this default from the moment its `StageClock` is constructed
   * (`stage-clock.ts`'s own `DEFAULT_DEMO_CLOCK_RATE`), so the initial value agrees with the world
   * by construction and the first `s` press cycles *from* 2x. Holding it in state costs one
   * re-render per press and still shows the audience nothing.
   */
  const [clockRate, setClockRate] = useState<DemoClockRate>(DEFAULT_DEMO_CLOCK_RATE);
  const [rateToast, setRateToast] = useState<DemoClockRate | null>(null);

  useEffect(() => {
    if (rateToast === null) return undefined;
    const handle = window.setTimeout(() => setRateToast(null), 1400);
    return () => window.clearTimeout(handle);
  }, [rateToast]);

  const requestFullscreenOnce = (): void => {
    if (fullscreenRequestedRef.current) {
      return;
    }
    fullscreenRequestedRef.current = true;
    const element = rootRef.current;
    if (element === null) {
      return;
    }
    // Degrades silently on rejection or absence (requirement "A refused fullscreen request
    // degrades silently") — nothing surfaced to the audience, and never requested again.
    void fullscreenApi.request(element).catch(() => undefined);
  };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      requestFullscreenOnce();
      // **The script-copy chord** (`Cmd`/`Ctrl`+`Shift`+`C`, "I've forgotten the script"). Handled
      // *before* the text-entry guard below, deliberately: the whole reason this exists is that
      // the presenter blanks with the cursor already in the B++ editor, which is exactly the
      // condition that guard exists to stop a bare key from reaching the sequencer under. Gated
      // tightly — meta/ctrl **and** shift **and** the literal `c` — so it can never swallow
      // ordinary typing or the browser's own `Cmd`/`Ctrl`+`C` (no shift) copy.
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'c') {
        event.preventDefault();
        const chapter = deriveUiState(sequencer.cursor, cueList ?? CUE_LIST).chapter;
        const program = chapterCannedProgram(chapter);
        if (program !== null) {
          void clipboardApi?.writeText(program).catch(() => undefined);
        }
        return; // never advances, reverses or jumps — a chord is not a cue key
      }
      /**
       * **A browser chord belongs to the browser** (deathmatch rehearsal, 2026-08-12 — plan
       * **D-1**). `Cmd+2` is "switch to browser tab 2", and the focused page gets the `keydown`
       * — `key === '2'` — *before* the tab switches. Tab-hopping to check whether Red and Blue
       * have joined is exactly what a presenter does during the join window, so the deck was
       * jumping to chapter 2 and re-announcing the chapter band in front of the room. `Cmd+R`
       * (reload) and `Cmd+S` (save) had the identical hole, and `Cmd+R` rebuilding the world
       * instead of reloading the page is the worse instance of it.
       *
       * Guarding the whole switch rather than the digits alone: no cue key wants a modifier. The
       * one keystroke that does — the copy chord — is evaluated above, deliberately ahead of
       * both this guard and the text-entry one.
       */
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isTextEntryFocused()) {
        return; // DD11: typing the B++ script or a prompt must never advance the presentation
      }
      switch (event.key) {
        case ' ':
        case 'Spacebar':
        case 'ArrowRight':
          event.preventDefault();
          moveCursor('advance', () => sequencer.advance());
          return;
        case 'ArrowLeft':
          event.preventDefault();
          // Reversing may rebuild the world (see `sequencer.reverse`); the cursor has already
          // moved either way, so the screen is repainted on both settle paths, like `advance`.
          moveCursor('reverse', () => sequencer.reverse());
          return;
        case '1':
        case '2':
        case '3':
          // A refused stage op rejects now (`demo-worker-host.ts`); it is reported to the
          // developer's console there, and the cursor has already moved, so the screen must
          // still be repainted either way.
          moveCursor('jump', () =>
            sequencer.jumpToChapter(
              `chapter${event.key}` as 'chapter1' | 'chapter2' | 'chapter3',
            ),
          );
          return;
        case RESET_KEY:
          moveCursor('reset', () => sequencer.reset());
          return;
        case SPEED_KEY:
          // No cursor movement, so no `forceRender` — but the rate IS state, because the renderer
          // has to be told how many world ticks a real second buys (see `clockRate`'s own note).
          // The functional update is what keeps the world and this value in step: the keydown
          // listener is installed once, under `[]` deps, so a closed-over `clockRate` would be
          // pinned at its mount value and every press would post the same second rate for ever.
          setClockRate((current) => {
            const next = nextDemoClockRate(current);
            driverRef.current?.setRate?.(next);
            setRateToast(next);
            return next;
          });
          return;
        default:
          return;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // `sequencer`, `requestFullscreenOnce` and `clipboardApi` are stable for this component's
    // lifetime (the sequencer is held in a ref, the fullscreen closure only reads refs/the
    // memoized api, and `clipboard`/`navigator.clipboard` do not change prop-to-prop in practice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cues = cueList ?? CUE_LIST;
  const uiState = deriveUiState(sequencer.cursor, cues);
  const scene = deriveScene(sequencer.cursor, cues);

  const hornPlayedRef = useRef(false);
  useEffect(() => {
    const slide = sequencer.currentSlide;
    if (
      slide === 'slide-pvp-introduction' &&
      !hornPlayedRef.current &&
      lastMoveRef.current !== 'reverse'
    ) {
      hornPlayedRef.current = true;
      const horn = createAudio(FESTIVAL_ASSETS.airhorn);
      void Promise.resolve(horn.play()).catch(() => undefined);
    }
  }, [createAudio, sequencer.currentSlide]);

  /**
   * **Stage chrome is painted over the stage and nowhere else** (E2E finding M10).
   *
   * `uiState.visible` accumulates within a chapter by design — that is what makes a jump or a reset
   * restore the HUD — but it says *what the chapter established*, not *what the projector should be
   * showing now*. Read without this gate, the open chapter-2 type editor, the Beekeeper prompt bar
   * and the two counters floated over the quote slide, the QR beat, the leaderboard and every
   * wrap-up slide, because nothing ever un-establishes them. The chapter title already had this
   * gate; the rest of the chrome now shares it, from one expression rather than five.
   */
  const onStage = scene.kind === 'stage';
  const showing = (token: UiToken): boolean => onStage && uiState.visible.has(token);
  const activeTitleToken = onStage ? uiState.activeTitle : null;
  const activeModal = onStage ? uiState.activeModal : null;
  const otherTokens = onStage
    ? [...uiState.visible].filter((token) => !SPECIALLY_RENDERED_TOKENS.has(token))
    : [];

  /**
   * **The chapter title is an arrival, not a state** (playthrough finding, 2026-08-09).
   *
   * `uiState.activeTitle` is derived from the cursor and says which chapter the cursor *stands in*
   * — which is the right derivation and is deliberately left alone (`ui-state.ts`). Rendered
   * directly, though, it made the title a property of position: reversing back across a title cue
   * re-mounted the full-width band over a world the presenter was navigating through, and the band
   * then settled into a corner card that stayed for the rest of the chapter.
   *
   * So *what is announced* is held here, at the render layer, and it is only ever set by **entering**
   * a chapter — a forward advance or a digit jump onto a new title. A reversal never sets it, a
   * stage reset (an unmoved cursor rebuilding its world) never sets it, and the band clears itself
   * through `onDone` once it has faded. Leaving every title — a chapter boundary, or the cursor
   * leaving the stage — clears it outright, so nothing can be left mid-fade to reappear later.
   */
  const [displayedTitle, setDisplayedTitle] = useState<ChapterTitleToken | null>(null);
  const previousTitleRef = useRef<ChapterTitleToken | null>(null);
  useEffect(() => {
    const previous = previousTitleRef.current;
    previousTitleRef.current = activeTitleToken;
    if (activeTitleToken === null) {
      setDisplayedTitle(null);
      return;
    }
    if (activeTitleToken === previous) {
      return; // still inside the chapter that was already announced
    }
    const move = lastMoveRef.current;
    if (move === 'advance' || move === 'jump') {
      setDisplayedTitle(activeTitleToken);
    }
  }, [activeTitleToken]);

  /** The world **the cursor's chapter staged** — read off the cue list's own `configure` anchor
   * (`cues.ts`), so the camera's frame and the type bar's chips describe whichever world the cursor
   * is actually standing in rather than a constant restated here. */
  const anchorStage = useMemo(() => {
    const anchor = configureAnchorIndex(cues, sequencer.cursor);
    const cue = anchor === undefined ? undefined : cues[anchor];
    return cue?.kind === 'stage' && cue.op.kind === 'configure' ? cue.op.stage : undefined;
  }, [cues, sequencer.cursor]);
  const ownColony = anchorStage?.colonies[DEMO_OWN_COLONY];
  const ownHive: readonly [number, number] | undefined =
    ownColony === undefined ? undefined : [ownColony.hive.x, ownColony.hive.y];
  /** What the stage camera frames (playthrough finding, 2026-08-09): the anchor's own hive and
   * patches, so each chapter is framed by what it actually put on the field. Not memoized — it is
   * five points of arithmetic, and `DemoStage` keys its camera on the frame's flat values precisely
   * so a fresh object per render cannot restart the draw loop. */
  const stageFrame = anchorStage === undefined ? undefined : stageFrameOf(anchorStage);
  /** Chapter 1's opening shot (2026-08-09 request): centred on the one staged bee — offset from
   * the hive, zoomed so the comb is off-screen — rather than on the whole staged rectangle. Only
   * chapter 1: the later chapters run on the same anchor world but want the colony in frame. */
  const stagedBee = ownColony?.bees[0];
  const stageFocus: readonly [number, number] | undefined =
    uiState.chapter === 'chapter1' && stagedBee !== undefined
      ? [stagedBee.position.x, stagedBee.position.y]
      : undefined;
  /** The staged types as *chips* — name and buff only. Dropping `program` is the point: chapter 2's
   * beat is the presenter typing into an empty editor, not reading the idling placeholder back. */
  const stagedChips = useMemo(
    () => (ownColony?.types ?? [STAGED_HARVESTER]).map(({ name, buff }) => ({ name, buff })),
    [ownColony],
  );

  /** Whether the cursor has reached a `fastForward` cue — the "if I keep applying this concept"
   * jump. Derived from the cursor like everything else, so it survives a reverse and a jump. */
  const fastForwardReached = useMemo(
    () =>
      cues
        .slice(0, sequencer.cursor + 1)
        .some((cue) => cue.kind === 'stage' && cue.op.kind === 'fastForward'),
    [cues, sequencer.cursor],
  );

  /**
   * **Whether the arena-filling beat has been reached** (E2E round-2 finding N-4, 2026-08-09).
   *
   * Three consecutive `nav` cues target the gathering chapter, and the cursor position is the only
   * fact that tells them apart — `deriveScene` collapses all three to the same `target`. `cursor >=
   * this index` rather than a "has ever passed" scan (contrast `fastForwardReached`, which is
   * deliberately one-way because the world it gates already ran): reversing the cue back past
   * `nav-awaiting-start` is meant to show the join surface again, not leave the interstitial stuck
   * on from an earlier pass.
   */
  const awaitingStartIndex = useMemo(
    () => cues.findIndex((cue) => cue.beatId === 'nav-awaiting-start'),
    [cues],
  );
  const awaitingStart = awaitingStartIndex !== -1 && sequencer.cursor >= awaitingStartIndex;

  // The two leaderboard-chapter `ui` cues are **presentation props on the real screen**, not
  // chrome of this component's own: `winnersPrompts` reveals the winner's own prompts on the
  // board, and `nextButton` is what gives the board somewhere to go — so the control exists
  // exactly from its cue and does exactly what the advance key does.
  const tournamentSeams =
    tournament === undefined
      ? undefined
      : {
          ...tournament,
          showWinnerPrompts: uiState.visible.has('winnersPrompts'),
          awaitingStart,
          onWatch: uiState.visible.has('nextButton')
            ? () => {
                // The board's own control does exactly what the advance key does, including
                // counting as a forward move for the chapter title's entry rule.
                moveCursor('advance', () => sequencer.advance());
              }
            : undefined,
        };

  // The counters' figures, read off the same snapshot stream the stage draws from. Held as
  // state (they are on screen) but updated only when a **displayed integer** moves, so a 15 Hz
  // stream does not re-render this subtree fifteen times a second to show the same numbers.
  // `typeCounts` is the same worker census broken down by wire type id — which is the type's
  // position in the colony's registration order — feeding the type bar's per-chip counts.
  const [counters, setCounters] = useState<{
    nectar: number | null;
    bees: number | null;
    typeCounts: readonly number[] | null;
  }>({ nectar: null, bees: null, typeCounts: null });
  useEffect(() => {
    const subscribe = stage?.subscribe;
    if (subscribe === undefined) {
      return undefined;
    }
    return subscribe((json) => {
      let world: {
        colonies?: { id: number; pool?: number; population?: number }[];
        bees?: { colony: number; type?: number | null }[];
      };
      try {
        world = JSON.parse(json);
      } catch {
        return; // a snapshot the stage itself will report — nothing for a counter to say
      }
      const own = (world.colonies ?? []).find((colony) => colony.id === DEMO_OWN_COLONY);
      if (own === undefined) {
        return;
      }
      // **The colony's workers, not its population** (E2E finding P3). `population` counts the
      // queen, so chapter 1's "one bee… now two… now ten" beats read 2, 3 and 11 under a
      // presenter saying otherwise. The queen carries no type — the same exclusion the
      // colony-wide `resume` and the instruct world read both already make.
      let workers = 0;
      const typeCounts: number[] = [];
      for (const bee of world.bees ?? []) {
        if (bee.colony === DEMO_OWN_COLONY && bee.type !== undefined && bee.type !== null) {
          workers += 1;
          typeCounts[bee.type] = (typeCounts[bee.type] ?? 0) + 1;
        }
      }
      const next = {
        nectar: own.pool === undefined ? null : Math.round(own.pool),
        bees: world.bees === undefined ? (own.population ?? null) : workers,
        typeCounts: world.bees === undefined ? null : typeCounts,
      };
      setCounters((current) =>
        current.nectar === next.nectar &&
        current.bees === next.bees &&
        (current.typeCounts ?? []).join() === (next.typeCounts ?? []).join() &&
        (current.typeCounts === null) === (next.typeCounts === null)
          ? current
          : next,
      );
    });
  }, [stage?.subscribe]);

  /**
   * **The colony's types once a Beekeeper turn has changed them** (E2E round-3 finding
   * V4-builder), or `null` while the staged list is still the truth.
   *
   * The type bar is read off the cue list's `configure` stage — which is a *static document*, so
   * the builder the script narrates the agent creating had no chip and no name anywhere on the
   * projector. The one component that learns what the agent wrote is the one that pushed it into
   * the world, so it reports it here. Deliberately **not** derived from the render snapshot: that
   * carries numeric type ids, never names, and a bar showing "type 1" is worse than no bar.
   */
  const [agentTypes, setAgentTypes] = useState<readonly DemoStageTypeSpec[] | null>(null);

  const [vocabulary, setVocabulary] = useState<readonly DemoVocabEntry[]>([]);
  useEffect(() => {
    if (uiState.activeModal !== 'fullVocabularyModal') {
      return;
    }
    const driver = driverRef.current;
    void driver
      ?.vocabulary?.()
      .then(setVocabulary)
      .catch(() => setVocabulary([]));
    // Only the modal opening matters — the vocabulary is a static parser export for the whole
    // run, not something that changes while the modal is up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.activeModal]);

  return (
    <div
      ref={rootRef}
      data-testid="demo-root"
      onClick={requestFullscreenOnce}
      // The projected 16:9 surface (E2E finding P2). `#root`'s own height:100% chain
      // (`ui/app-shell.css`) stops resolving the moment it reaches an unstyled block-level div —
      // percentage heights need a definite-height ancestor, and this was one — so the slide below
      // (itself `height:100%`) collapsed to its own content height instead of the viewport,
      // leaving whatever sits behind the page showing as a void under every slide. `position:
      // relative` plus an explicit paper background makes this div both the positioning root for
      // `demo-slide`'s `inset: 0` (below) and the page's own backstop, so nothing behind it can
      // ever show through.
      style={{ position: 'relative', width: '100%', height: '100%', background: 'var(--paper-0)' }}
    >
      {/* The scene is derived, not read off the cue at the cursor: a `reveal` steps the slide
          already up and a `ui` cue arrives over the world already running (`ui-state.ts`). */}
      {scene.kind === 'slide' && sequencer.currentSlide !== null && (
        // `data-slide`/`data-reveal` are **attributes, not chrome** — nothing an audience can see,
        // and nothing the presenter can act on. They exist so the rehearsal walk (task 13.4) can
        // assert *which* slide is up at *which* reveal step, which is the only observable a slide
        // beat and a reveal beat actually have.
        //
        // `position: absolute; inset: 0` (the same pattern `demo-stage` already uses below) rather
        // than relying on a percentage-height chain through this wrapper: it fills `demo-root`
        // exactly regardless of what any ancestor's `height` resolves to (E2E finding P2).
        <div
          data-testid="demo-slide"
          data-slide={sequencer.currentSlide}
          data-reveal={sequencer.revealStep}
          style={{ position: 'absolute', inset: 0 }}
        >
          {sequencer.currentSlide !== 'slide-cover' &&
            sequencer.currentSlide !== 'slide-finisher' && (
              <img
                data-testid="festival-slide-element"
                className="festival-element-enter"
                src={FESTIVAL_ASSETS.elementTwo}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  zIndex: Z_LOCAL.coverElement,
                  width: 'auto',
                  height: 250,
                  right: -200,
                  bottom: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
          {renderSlide(sequencer.currentSlide, sequencer.revealStep)}
        </div>
      )}
      {exitingCover && (
        <div
          data-testid="festival-cover-exit"
          className="festival-cover-exit"
          style={{ position: 'absolute', inset: 0, zIndex: Z.coverExit, pointerEvents: 'none' }}
        >
          {renderSlide('slide-cover', 0)}
        </div>
      )}
      {onStage && (
        <div data-testid="demo-stage" style={{ position: 'absolute', inset: 0 }}>
          <DemoStage
            ownColonyId={DEMO_OWN_COLONY}
            ownHive={ownHive}
            stageFrame={stageFrame}
            focus={stageFocus}
            tickRateHz={DEMO_TICK_HZ}
            playbackRate={clockRate}
            subscribe={stage?.subscribe}
            createRenderer={stage?.createRenderer}
            camera={stage?.camera}
            frameScheduler={stage?.frameScheduler}
            clock={stage?.clock}
            submitCommand={(op) => void (driverRef.current ?? INERT_STAGE_DRIVER).submitCommand(op)}
          />
        </div>
      )}
      {rateToast !== null && (
        <div
          className="demo-rate-toast"
          data-testid="demo-rate-toast"
          role="status"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: Z.toast,
            borderRadius: 'var(--r-pill)',
            background: 'var(--hud-bg)',
            color: 'var(--hud-text)',
            border: '1px solid var(--hud-line)',
            padding: '8px 14px',
            fontFamily: 'var(--font-display)',
            fontSize: 20,
          }}
        >
          {rateToast}×
        </div>
      )}
      {/* Mounted once per chapter and held mounted thereafter (design **DD9**) — so `target` is
          what the cursor says now, and the component decides what stays alive behind it. */}
      {presentation === undefined ? (
        <DemoTournamentChapters target={scene.kind === 'nav' ? scene.target : null} seams={tournamentSeams} />
      ) : (
        <DemoLiveMatchChapters
          target={scene.kind === 'nav' ? scene.target : null}
          seams={presentation}
          // Starting the match and showing the match are one action (plan **D-3**) — the
          // presenter used to have to remember a Space press after the click.
          onStarted={() => moveCursor('jump', () => sequencer.jumpToBeat(LIVE_MATCH_BEAT))}
          // ...and resetting it puts the room back in front of the join window, in one action too.
          onReset={() => moveCursor('jump', () => sequencer.jumpToBeat(JOIN_WINDOW_BEAT))}
        />
      )}
      <div data-testid="demo-hud">
        <DemoCounters
          // The nectar counter is up from the **configure**, so the game opens with it in the
          // top-right rather than costing the presenter a second advance (2026-08-09 request).
          // That used to be expressed here as `showNectar={onStage}` — this one element gated
          // differently from the other three — with its `ui` cue left in the list as an inert beat.
          // The cue is now deleted and `deriveUiState` establishes the token on the configure
          // instead (`ui-state.ts`), so all four HUD elements are read the same way and `visible`
          // describes the whole HUD. `showing()` still folds in `onStage`, so leaving the stage
          // entirely removes it exactly as before.
          showNectar={showing('nectarCounter')}
          showBees={showing('beeCounter')}
          nectar={counters.nectar}
          bees={counters.bees}
        />
        {showing('typeBar') && (
          <div
            data-testid="hud-typeBar"
            // **The gap above the prompt bar, tuned against its tallest state, not its resting one**
            // (E2E round-2 finding N-5, 2026-08-09). `PromptBar`'s own `BAR_STYLE` bottom-anchors it
            // at `bottom: 0` with no `top`, so — exactly like this box — its height is whatever its
            // content needs and its top edge is what moves. At rest that content is one input row:
            // measured 71px tall at 1920×1080, comfortably under the 74px this box used to reserve.
            // But `uiState.visible` accumulates (this file's own comment on `dismissKey`, and
            // `InstructPromptBar`'s "Keyed by chapter" note): chapter 1b's "Bees" bar is still the
            // one on screen when chapter 2's type bar appears, and a submitted prompt leaves either
            // a failure line (`prompt-bar-failure`) or a reply line (`prompt-bar-reply`) sitting
            // under its input row from then on. Measured with one showing: 92.5px, 21.5px past the
            // 74px gap — enough for `hud-promptBar`, rendered after this div in `demo-hud` and
            // sharing its `Z.hudBar` layer, to paint over the *bottom* ~18.5px of `code-edit-save` and
            // `code-edit-cancel` (both 40px tall, flush with this box's own bottom edge): confirmed
            // with `elementFromPoint` inside the Save button's rect resolving to `hud-promptBar`,
            // not the button, once the failure line was up. 100 clears the measured 92.5px with a
            // few px to spare, the same small-clearance margin the original 74-vs-71 pairing kept.
            style={{ position: 'absolute', left: 0, right: 0, bottom: 100, zIndex: Z.hudBar }}
          >
            <TypeBar
              driver={driverRef.current ?? INERT_STAGE_DRIVER}
              colony={DEMO_OWN_COLONY}
              // The types the colony actually has — name *and* buff, because the editor composes
              // the compiler's `type … buffs …` header from them (`TypeEditor.tsx`). The staged
              // list until a Beekeeper turn changes it, and the agent's own list after; the staged
              // entries pass **no** program, so chapter 2's editor still opens blank for the
              // presenter to type into, while the agent's chips open on what the agent wrote.
              // `count` is the snapshot census at the type's registration index — the same order
              // the wire's numeric type ids follow.
              types={(agentTypes ?? stagedChips).map((type, index) => ({
                ...type,
                count: counters.typeCounts === null ? null : (counters.typeCounts[index] ?? 0),
              }))}
              // A chapter change closes whatever panel is open: chapter 2's editor floated over
              // chapter 3's world, its fast-forward and the full-hive reveal (E2E finding N6).
              dismissKey={uiState.chapter}
            />
          </div>
        )}
        {showing('promptBar') &&
          (uiState.visible.has('promptScopeBeekeeper') ? (
            <BeekeeperPromptBar
              driver={driverRef.current ?? INERT_STAGE_DRIVER}
              birthType={ownColony?.birthType ?? 'harvester'}
              seedTypes={ownColony?.types ?? []}
              // **The reveal is the picture** (E2E round-3 polish): the chat overlay sat over the
              // fast-forward and the full-hive reveal until someone found its ✕. The fast-forward
              // cue *is* the moment the beat stops being a conversation.
              collapseChat={fastForwardReached}
              onTypesApplied={setAgentTypes}
              baseUrl={agents?.baseUrl}
              {...agents?.beekeeper}
            />
          ) : (
            <InstructPromptBar
              // **Keyed by chapter** (E2E finding N7): the bar holds its own failure, draft and
              // reply, and `uiState.visible` deliberately accumulates, so chapter 1's "Couldn't
              // reach the hive." rode under every chapter-2 and chapter-3 screen for the rest of
              // the show. A chapter boundary is exactly where that state stops being true.
              key={uiState.chapter ?? 'none'}
              driver={driverRef.current ?? INERT_STAGE_DRIVER}
              // The finer-grained clear, within a chapter (E2E round-1 finding, 2026-08-09) — see
              // `InstructPromptBar.tsx`'s own note on why the chapter key alone was not enough.
              cursor={sequencer.cursor}
              baseUrl={agents?.baseUrl}
              {...agents?.instruct}
            />
          ))}
        {displayedTitle !== null && (
          <ChapterTitleOverlay
            key={displayedTitle}
            kicker={CHAPTER_TITLES[displayedTitle].kicker}
            text={CHAPTER_TITLES[displayedTitle].text}
            onDone={() => setDisplayedTitle(null)}
          />
        )}
        {/* Every other visible `ui` token (the rounds-mode chrome tokens groups 10/12 own) —
            unowned here, so it still registers as present rather than vanishing silently. */}
        {otherTokens.map((token) => (
          <div key={token} data-testid={`hud-${token}`} />
        ))}
      </div>
      {activeModal === 'sixActionsModal' && <SixActionsModal />}
      {activeModal === 'fullVocabularyModal' && <FullVocabularyModal entries={vocabulary} />}
      {/* The program the agent actually wrote — `agentTypes` is what the Beekeeper bar reported
          after applying its turn, so an un-run chapter 3 shows the modal's own "no program yet"
          rather than a canned illustration standing in for the beat. */}
      {activeModal === 'builderFlowchartModal' && (
        <BuilderFlowchartModal types={agentTypes ?? []} />
      )}
      {/* Last child, inside the fullscreen element — see `DemoAppProps.overlay`. */}
      {overlay}
      {/* No placeholder text surface. There used to be a bare `<textarea>` here, standing in for
          the prompt bar group 10 had not built yet — and it outlived its stand-in duty, rendering
          unstyled in the top-left corner of every slide and every tournament screen for the whole
          show (E2E finding M12). The real prompt bar and the real `CodeView` editor are both
          mounted above, and `text-entry-guard.ts` is generic over any focused text surface, so
          nothing needed it. */}
    </div>
  );
}
